// ============================================
// useVolumeAlerts.js  (volalert-20261028)
// Client-side volume alerts for the bottom Volume bars. An alert is anchored
// to one bar (its startTime) at a volume level; it fires the first time a
// LATER bar's total volume reaches the level (the live bar counts the moment
// it gets there; it does not wait for the close). On fire: bell + toast +
// browser Notification, then the alert is removed (same as price alerts).
// Stored per instrument + timeframe in localStorage, so they only run while
// that chart is open (no server copy).
// ============================================
import { useCallback, useRef, useState } from 'react'
import { playBell } from './usePriceAlerts'

const PREFIX = 'solcheddar_volalerts_'
const SETTLE_MS = 4000   // ignore checks right after a token/timeframe switch

export function fmtVol(v) {
  if (v == null || !Number.isFinite(v)) return '--'
  const a = Math.abs(v)
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`
  return a >= 10 ? `${Math.round(v)}` : `${+v.toFixed(2)}`
}

function load(key) {
  if (!key) return []
  try {
    const a = JSON.parse(localStorage.getItem(key) || '[]')
    return Array.isArray(a) ? a.filter(x => x && Number.isFinite(x.t) && Number.isFinite(x.level) && x.level > 0) : []
  } catch { return [] }
}

export default function useVolumeAlerts(token, interval, opts = {}) {
  const key = token ? `${PREFIX}${token}_${interval || 60}` : null
  const optsRef = useRef(opts)
  optsRef.current = opts
  const keyRef = useRef(undefined)
  const alertsRef = useRef([])
  const settleRef = useRef(0)
  const [, setVer] = useState(0)
  const [notifications, setNotifications] = useState([])
  // Load synchronously on key change so a draw never checks token A's
  // alerts against token B's candles.
  if (keyRef.current !== key) {
    keyRef.current = key
    alertsRef.current = load(key)
    settleRef.current = Date.now() + SETTLE_MS
  }
  const commit = useCallback((next, deferRedraw = false) => {
    alertsRef.current = next
    const k = keyRef.current
    if (k) {
      try {
        if (next.length) localStorage.setItem(k, JSON.stringify(next))
        else localStorage.removeItem(k)
      } catch {}
    }
    setVer(v => v + 1)
    // From inside a draw (check) the repaint must not re-enter it.
    if (deferRedraw) requestAnimationFrame(() => optsRef.current.redraw?.())
    else optsRef.current.redraw?.()
  }, [])

  const add = useCallback((t, level) => {
    if (!Number.isFinite(t) || !Number.isFinite(level) || level <= 0) return
    commit([...alertsRef.current, { id: `va_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`, t, level, createdAt: Date.now() }])
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission() } catch {}
    }
  }, [commit])
  const remove = useCallback((id) => commit(alertsRef.current.filter(a => a.id !== id)), [commit])
  const update = useCallback((id, level) => {
    if (!Number.isFinite(level) || level <= 0) return
    commit(alertsRef.current.map(a => a.id === id ? { ...a, level } : a))
  }, [commit])
  const dismiss = useCallback((id) => setNotifications(p => p.filter(n => n.id !== id)), [])

  // Called from the chart's full draw with the current candles. A bar
  // triggers when it starts after the anchor, was still forming when the
  // alert was set or later (so old bars that already beat the level don't
  // fire it instantly), and its volume >= level.
  const check = useCallback((candles, ivMs, volOf) => {
    const list = alertsRef.current
    if (!list.length || !candles || !candles.length || Date.now() < settleRef.current) return
    const fired = []
    for (const a of list) {
      for (let i = candles.length - 1; i >= 0; i--) {
        const c = candles[i]
        const st = c?.startTime
        if (!Number.isFinite(st) || st <= a.t) break
        if (st + ivMs <= a.createdAt) continue
        const v = volOf(c)
        if (v >= a.level) { fired.push({ a, v }); break }
      }
    }
    if (!fired.length) return
    const ids = new Set(fired.map(f => f.a.id))
    commit(alertsRef.current.filter(a => !ids.has(a.id)), true)
    const label = optsRef.current.getLabel?.() || ''
    for (const { a, v } of fired) {
      const id = `vol_${Date.now()}_${a.id}`
      const n = { id, kind: 'vol', title: 'Volume Alert', accent: 'amber', line: `${label} vol ≥ ${fmtVol(a.level)}`.trim(), sub: `bar hit ${fmtVol(v)} · click to dismiss`, level: a.level, vol: v }
      setNotifications(p => [...p, n])
      setTimeout(() => setNotifications(p => p.filter(x => x.id !== id)), 12000)
      try { optsRef.current.onTrigger?.(n) } catch {}
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification('SolCheddar Volume Alert', { body: `${label} volume ≥ ${fmtVol(a.level)} (${fmtVol(v)})`.trim(), tag: a.id })
        }
      } catch {}
    }
    playBell()
  }, [commit])

  return { alertsRef, alerts: alertsRef.current, add, remove, update, check, notifications, dismiss }
}

// 3 significant figures: drag / bar levels read cleanly in the input.
export function roundVol(v) {
  if (!Number.isFinite(v) || v <= 0) return 0
  return Number(v.toPrecision(3))
}
